/* eslint-disable no-restricted-syntax */

import { IPlanning, IPlanningList, UpdatePlanning } from "../../types/planning";

/**
 * Stable identity for a Planning tree row.
 *
 * `planning_overview_id` is null for people who do not have an overview yet.
 * Using it directly as Ant Design's row key makes every such person the same
 * row, so expanding one expands all of them. The planning record and person
 * IDs are stable fallbacks until an overview exists.
 */
export const getPlanningRowKey = (planning: IPlanning): string =>
  planning?.planning_overview_id ||
  planning?.id ||
  planning?.responsible_person?.id ||
  "";

export const getClickedOverviewInfo: (
  planningList: IPlanningList,
  targetKey: string
) => null | IPlanning = (
  planningListData: IPlanningList,
  targetKey: string
) => {
  for (const planning of planningListData) {
    if (getPlanningRowKey(planning) === targetKey) {
      return planning;
    }
    if (planning?.children && planning?.children?.length > 0) {
      const clickedMenuItem: IPlanning | null = getClickedOverviewInfo(
        planning.children,
        targetKey
      );
      if (clickedMenuItem) {
        return clickedMenuItem;
      }
    }
  }
  return null;
};

export const getPlanningByPersonId: (
  planningList: IPlanningList,
  personId: string
) => null | IPlanning = (planningListData: IPlanningList, personId: string) => {
  for (const planning of planningListData) {
    if (planning?.responsible_person?.id === personId) {
      return planning;
    }
    if (planning?.children && planning?.children?.length > 0) {
      const clickedMenuItem: IPlanning | null = getPlanningByPersonId(
        planning.children,
        personId
      );
      if (clickedMenuItem) {
        return clickedMenuItem;
      }
    }
  }
  return null;
};

export const findOverviewAndUpdateCheckbox: (
  planningListData: IPlanningList,
  targetKey: string,
  updatedPlanning: UpdatePlanning
) => IPlanningList = (planningListData, targetKey, updatedPlanning) => {
  return planningListData?.map((planning) => {
    if (getPlanningRowKey(planning) === targetKey) {
      return { ...planning, ...updatedPlanning };
    }
    if (planning.children && planning.children.length > 0) {
      return {
        ...planning,
        children: findOverviewAndUpdateCheckbox(
          planning.children,
          targetKey,
          updatedPlanning
        )};
    }
    return planning;
  });
};

export const sortByCEO = (data: IPlanning[]): IPlanning[] => {
  return [...data].sort((a, b) => {
    const aC = a.responsible_person?.ceo_level ? 1 : 0;
    const bC = b.responsible_person?.ceo_level ? 1 : 0;
    return bC - aC;
  });
};

export const findOverviewAndUpdateChildren: (
  planningListData: IPlanningList,
  targetKey: string,
  childData: IPlanningList
) => IPlanningList = (planningListData, targetKey, childData) => {
  return planningListData?.map((planning) => {
    if (getPlanningRowKey(planning) === targetKey) {
      const children = childData?.map((overview) => {
        return {
          ...overview,
          key: getPlanningRowKey(overview),
          ...(overview?.responsible_person?.reportees?.length === 0
            ? {}
            : { children: [] })};
      });
      return { ...planning, children };
    }
    if (planning.children && planning.children.length > 0) {
      const updatedChildren = findOverviewAndUpdateChildren(
        planning.children,
        targetKey,
        childData
      );
      return {
        ...planning,
        ...(updatedChildren?.length === 0 ? {} : { children: updatedChildren })};
    }
    return planning;
  });
};
